<template>
  <div class="ofs-orphan">
    <Bars3Icon aria-hidden="true" class="ofs-orphan__handle" :class="{ 'handle': !deleting }" />
    <span class="ofs-orphan__name" :class="{ 'handle': !deleting }">{{ name }}</span>
    <UIDropdownConfirm
      :disabled="deleting"
      placement="bottom-end"
      message="Are you sure you want to delete the account?"
      @confirm="handleDelete"
    >
      <template #default="{ toggle }">
        <UIButton
          v-tooltip.top="'Delete account'"
          variant="danger"
          fill="light"
          size="xxs"
          :disabled="deleting"
          :loading="deleting"
          :leading="TrashIcon"
          @click="toggle"
        />
      </template>
      <template #after>
        <div class="ofs-orphan__delete-info">
          <InformationCircleIcon aria-hidden="true" />
          All of your organize family structure change history will be lost
        </div>
      </template>
    </UIDropdownConfirm>
    <MoveTo v-if="data" :data @select="handleSelect" />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue'

import { UNKNOWN_VALUE } from '@/const'
import { DropdownListData } from '@types'

import { TrashIcon, Bars3Icon } from '@heroicons/vue/24/outline'
import MoveTo from './components/MoveTo.vue'
import { UIButton } from '@ui/buttons'
import { UIDropdownConfirm } from '@ui/dropdowns'
import { InformationCircleIcon } from '@heroicons/vue/24/outline'

type Props = {
  element: string
  data?: DropdownListData[]
}

type Emits = {
  'select': [value: string]
  'delete': [value: string, callback: () => void]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
  
const nameFormatter = inject<(name: string) => string>('nameFormatter')

const deleting = ref(false)

const name = computed(() => nameFormatter?.(props.element) || UNKNOWN_VALUE)

const handleSelect = (value: string) => {
  emit('select', value)
}

const handleDelete = () => {
  deleting.value = true
  emit('delete', props.element, () => {
    deleting.value = false
  })
}
</script>

<style>
.ofs-area__unorganized .ofs-area__tree-item-name,
.ofs-orphan {
  @apply flex items-center justify-between shrink-0;
  @apply py-1.5 px-2.5 gap-2 pr-1;
  @apply border border-common-border;
  @apply rounded-md;
  @apply bg-body;
  @apply text-sm;
  @apply truncate;

  &__name {
    @apply flex-auto;
    @apply truncate;
  }

  &__handle {
    @apply size-4;
    @apply shrink-0;
  }

  &__delete-info {
    @apply mt-2 -mb-1;
    @apply flex items-start gap-1;
    @apply opacity-50;
    @apply text-[0.625rem];
    @apply text-left leading-tight;

    svg {
      @apply size-4;
      @apply -mt-0.5;
      @apply shrink-0;
    }
  }

  .handle {
    @apply cursor-move;
  }
}

.ofs-area__unorganized .ofs-area__tree-item-name {
  @apply opacity-50;
}
</style>
